import { PortfolioFilterSet, PortfolioGridField, PortfolioGridFilter, PortfolioGridGroup, ReportDateHelper, TemplateHelpers } from "../Common/KendoHelpers";
import { Ajax } from "../Common/Ajax";
import { GlobalData } from "../Common/GlobalData";
import { TooltipColumnContent, TooltipElement, TooltipHelper } from "../Common/TooltipHelper";

export class MultiSourceAnalysis {
    protected static readonly GROUP_COMPANY = 'Company';
    protected static readonly COMPANY_SWITCH = 'companySwitch';
    protected static readonly GROUP_INDUSTRY = 'Industry';
    protected static readonly INDUSTRY_SWITCH = 'industrySwitch';
    protected static readonly GROUP_PRICE = 'Price';
    protected static readonly GROUP_ATR = 'ATR';
    protected static readonly GROUP_TECH_RS = 'Tech RS';
    protected static readonly GROUP_TECH = 'Tech';
    protected static readonly GROUP_TECH_MS = 'Tech MS';
    protected static readonly GROUP_EPS = 'EPS';
    protected static readonly GROUP_MS_DATA = 'MS Data';

    protected static readonly FILTER_MONTHLY = 'monthly';
    protected static readonly FILTER_WEEKLY = 'weekly';
    protected static readonly FILTER_WEBBY = 'webby';

    protected readonly gridId: string;

    protected grid!: kendo.ui.Grid;
    protected filterSet!: PortfolioFilterSet;
    protected fields: { [id: string]: PortfolioGridField; } = {};
    protected dataSourceReadUrl = '';
    protected rowCounter: number = 0;

    public constructor(gridId: string) {
        this.gridId = gridId;
    }

    public init(dataSourceReadUrl: string) {
        this.dataSourceReadUrl = dataSourceReadUrl;

        this.initFields();
        this.initFilters();

        const dataSource = this.getGridDataSource();
        const gridOptions = this.getGridOptions(dataSource);
        this.grid = $(this.gridId).kendoGrid(gridOptions).data('kendoGrid')!;

        this.filterSet = new PortfolioFilterSet(this.grid, this.fields);
        this.filterSet.setGroupState(MultiSourceAnalysis.GROUP_PRICE, false);
    }

    protected initFields() {
        this.fields['symbol'] = new PortfolioGridField('symbol');
        this.fields['msCompanyName'] = new PortfolioGridField('msCompanyName',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_COMPANY, true));
        this.fields['msCompanyDescription'] = new PortfolioGridField('msCompanyDescription');
        this.fields['vlCompanyName'] = new PortfolioGridField('vlCompanyName',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_COMPANY, false));
        this.fields['personalReportGrade'] = new PortfolioGridField('personalReportGrade');
        this.fields['personalReportDate'] = new PortfolioGridField('personalReportDate');

        this.fields['msIndustryName'] = new PortfolioGridField('msIndustryName',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_INDUSTRY, true));
        this.fields['msIndustryRank'] = new PortfolioGridField('msIndustryRank',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_INDUSTRY, true));
        this.fields['vlIndustryName'] = new PortfolioGridField('vlIndustryName',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_INDUSTRY, false));
        this.fields['vlIndustryRank'] = new PortfolioGridField('vlIndustryRank',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_INDUSTRY, false));

        this.fields['currentPrice'] = new PortfolioGridField('currentPrice');
        this.fields['dailyClosingRange'] = new PortfolioGridField('dailyClosingRange',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_PRICE, true));
        this.fields['weeklyClosingRange'] = new PortfolioGridField('weeklyClosingRange',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_PRICE, true));
        this.fields['priceVsTwentyOneDay'] = new PortfolioGridField('priceVsTwentyOneDay',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_PRICE, true));
        this.fields['priceVsFiftyDay'] = new PortfolioGridField('priceVsFiftyDay',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_PRICE, true));
        this.fields['priceVsTwoHundredDay'] = new PortfolioGridField('priceVsTwoHundredDay',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_PRICE, true));
        this.fields['movingAveragesGrade'] = new PortfolioGridField('movingAveragesGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_PRICE, false));

        this.fields['relativeStrengthRating'] = new PortfolioGridField('relativeStrengthRating',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['relativeStrengthSixMonthRating'] = new PortfolioGridField('relativeStrengthSixMonthRating',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['relativeStrengthThreeMonthRating'] = new PortfolioGridField('relativeStrengthThreeMonthRating',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['accumDistRating'] = new PortfolioGridField('accumDistRating',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['accumDistRatingGrade'] = new PortfolioGridField('accumDistRatingGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['relativeStrengthGrade'] = new PortfolioGridField('relativeStrengthGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, false));
        this.fields['relativeStrengthStacked'] = new PortfolioGridField('relativeStrengthStacked',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['relativeStrengthPositive'] = new PortfolioGridField('relativeStrengthPositive',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['relativeStrengthLineNewHigh'] = new PortfolioGridField('relativeStrengthLineNewHigh',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['fullCurrentPctK'] = new PortfolioGridField('fullCurrentPctK',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['fullCurrentPctKGrade'] = new PortfolioGridField('fullCurrentPctKGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, true));
        this.fields['rs8WeekCrossoverGrade'] = new PortfolioGridField('rs8WeekCrossoverGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, false));
        this.fields['rs8WeekCrossoverWeeksAgo'] = new PortfolioGridField('rs8WeekCrossoverWeeksAgo',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, false));
        this.fields['rs21WeekCrossoverGrade'] = new PortfolioGridField('rs21WeekCrossoverGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, false));
        this.fields['rs21WeekCrossoverWeeksAgo'] = new PortfolioGridField('rs21WeekCrossoverWeeksAgo',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_TECH_RS, false));

        this.fields['twentyOneDayAtrPct'] = new PortfolioGridField('twentyOneDayAtrPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_ATR, true));
        this.fields['thirtyDayAtrPct'] = new PortfolioGridField('thirtyDayAtrPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_ATR, true));
        this.fields['fiftyDayAtrPct'] = new PortfolioGridField('fiftyDayAtrPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_ATR, true));
        this.fields['earningsStability'] = new PortfolioGridField('earningsStability',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_ATR, true));
        this.fields['atrGrade'] = new PortfolioGridField('atrGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_ATR, false));

        this.fields['earningsPerShareRating'] = new PortfolioGridField('earningsPerShareRating');
        this.fields['earningsPerShareGrade'] = new PortfolioGridField('earningsPerShareGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_EPS, false));
        this.fields['earningsPerSharePctGrowthThreeYr'] = new PortfolioGridField('earningsPerSharePctGrowthThreeYr',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_EPS, true));
        this.fields['earningsPerShareEstCurYrPct'] = new PortfolioGridField('earningsPerShareEstCurYrPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_EPS, true));
        this.fields['earningsPerShareNextYrPct'] = new PortfolioGridField('earningsPerShareNextYrPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_EPS, true));

        this.fields['compositeRating'] = new PortfolioGridField('compositeRating');
        this.fields['smrRating'] = new PortfolioGridField('smrRating');
        this.fields['returnOnEquity'] = new PortfolioGridField('returnOnEquity');
        this.fields['returnOnEquityGrade'] = new PortfolioGridField('returnOnEquityGrade');

        this.fields['baseTypeStage'] = new PortfolioGridField('baseTypeStage');
        this.fields['eventDate'] = new PortfolioGridField('eventDate');
        this.fields['weeklyPctChgVsPivot'] = new PortfolioGridField('weeklyPctChgVsPivot');
        this.fields['baseFormingGrade'] = new PortfolioGridField('baseFormingGrade');
        this.fields['baseFormingWeeksAgo'] = new PortfolioGridField('baseFormingWeeksAgo');

        this.fields['numBlueDots'] = new PortfolioGridField('numBlueDots',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_MS_DATA, true));
        this.fields['antsCount'] = new PortfolioGridField('antsCount',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_MS_DATA, true));
        this.fields['yieldPct'] = new PortfolioGridField('yieldPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_MS_DATA, true));
        this.fields['debtPct'] = new PortfolioGridField('debtPct',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_MS_DATA, true));
        this.fields['debtPctGrade'] = new PortfolioGridField('debtPctGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_MS_DATA, true));
        this.fields['incomeQualityGrade'] = new PortfolioGridField('incomeQualityGrade',
            new PortfolioGridGroup(MultiSourceAnalysis.GROUP_MS_DATA, false));

        this.fields['timePerf'] = new PortfolioGridField('timePerf');
        this.fields['timePerfGrade'] = new PortfolioGridField('timePerfGrade');
        this.fields['eighteenMonthPctToMid'] = new PortfolioGridField('eighteenMonthPctToMid');
        this.fields['eighteenMonthPctToMidGrade'] = new PortfolioGridField('eighteenMonthPctToMidGrade');

        this.fields['minervini'] = new PortfolioGridField('minervini');
        this.fields['totalValue'] = new PortfolioGridField('totalValue');
    }

    protected initFilters() {
        this.fields['currentPrice'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY,
            new PortfolioGridFilter('currentPrice', 'ge', 10));
        this.fields['priceVsTwoHundredDay'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY,
            new PortfolioGridFilter('priceVsTwoHundredDay', 'ge', 0));
        this.fields['earningsStability'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY + '1',
            new PortfolioGridFilter('earningsStability', 'gt', 0));
        this.fields['earningsStability'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY + '2',
            new PortfolioGridFilter('earningsStability', 'le', 17));
        this.fields['timePerf'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY,
            new PortfolioGridFilter('timePerf', 'le', 3));
        this.fields['debtPct'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY,
            new PortfolioGridFilter('debtPct', 'le', 0.6));

        this.fields['currentPrice'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('currentPrice', 'ge', 10));
        this.fields['weeklyClosingRange'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('weeklyClosingRange', 'ge', 0.4));
        this.fields['priceVsFiftyDay'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('priceVsFiftyDay', 'ge', 0));
        this.fields['priceVsTwoHundredDay'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('priceVsTwoHundredDay', 'ge', 0));
        this.fields['relativeStrengthRating'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('relativeStrengthRating', 'ge', 80));
        this.fields['accumDistRating'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('accumDistRating', 'lt', 'D'));
        this.fields['compositeRating'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('compositeRating', 'ge', 85));
        this.fields['earningsPerShareRating'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('earningsPerShareRating', 'ge', 85));

        this.fields['accumDistRating'].addFilter(MultiSourceAnalysis.FILTER_WEBBY,
            new PortfolioGridFilter('accumDistRating', 'le', 'B'));
        this.fields['compositeRating'].addFilter(MultiSourceAnalysis.FILTER_WEBBY,
            new PortfolioGridFilter('compositeRating', 'ge', 90));
        this.fields['smrRating'].addFilter(MultiSourceAnalysis.FILTER_WEBBY,
            new PortfolioGridFilter('smrRating', 'le', 'B'));
        this.fields['earningsPerShareRating'].addFilter(MultiSourceAnalysis.FILTER_WEBBY,
            new PortfolioGridFilter('earningsPerShareRating', 'ge', 80));
        this.fields['relativeStrengthRating'].addFilter(MultiSourceAnalysis.FILTER_WEBBY,
            new PortfolioGridFilter('relativeStrengthRating', 'ge', 80));
    }

    protected getGridDataSource(): any {
        const dataSource = {
            transport: {
                read: this.dataSourceReadUrl,
            },
            batch: true,
            serverPaging: false,
            serverSorting: false,
            schema: {
                model: {
                    fields: {
                        symbol: { type: 'string' },
                        personalReportGrade: { type: 'string' },
                        personalReportDate: { type: 'date' },
                        currentPrice: { type: 'number' },
                        dailyClosingRange: { type: 'number' },
                        weeklyClosingRange: { type: 'number' },
                        priceVsTwentyOneDay: { type: 'number' },
                        priceVsFiftyDay: { type: 'number' },
                        priceVsTwoHundredDay: { type: 'number' },
                        movingAveragesGrade: { type: 'string' },
                        relativeStrengthRating: { type: 'number' },
                        relativeStrengthSixMonthRating: { type: 'number' },
                        relativeStrengthThreeMonthRating: { type: 'number' },
                        relativeStrengthGrade: { type: 'string' },
                        accumDistRating: { type: 'string' },
                        accumDistRatingGrade: { type: 'string' },
                        relativeStrengthStacked: { type: 'boolean' },
                        relativeStrengthPositive: { type: 'boolean' },
                        relativeStrengthLineNewHigh: { type: 'boolean' },
                        fullCurrentPctK: { type: 'number' },
                        fullCurrentPctKGrade: { type: 'string' },
                        rs8WeekCrossoverGrade: { type: 'string' },
                        rs8WeekCrossoverWeeksAgo: { type: 'number' },
                        rs21WeekCrossoverGrade: { type: 'string' },
                        rs21WeekCrossoverWeeksAgo: { type: 'number' },
                        twentyOneDayAtrPct: { type: 'number' },
                        thirtyDayAtrPct: { type: 'number' },
                        fiftyDayAtrPct: { type: 'number' },
                        earningsStability: { type: 'number' },
                        atrGrade: { type: 'string' },
                        earningsPerShareRating: { type: 'number' },
                        earningsPerShareGrade: { type: 'string' },
                        earningsPerSharePctGrowthThreeYr: { type: 'number' },
                        earningsPerShareEstCurYrPct: { type: 'number' },
                        earningsPerShareNextYrPct: { type: 'number' },
                        compositeRating: { type: 'number' },
                        returnOnEquity: { type: 'number' },
                        returnOnEquityGrade: { type: 'string' },
                        baseTypeStage: { type: 'string' },
                        eventDate: { type: 'date' },
                        weeklyPctChgVsPivot: { type: 'number' },
                        baseFormingGrade: { type: 'string' },
                        baseFormingWeeksAgo: { type: 'number' },
                        numBlueDots: { type: 'number' },
                        antsCount: { type: 'number' },
                        yieldPct: { type: 'number' },
                        debtPct: { type: 'number' },
                        dbtPctGrade: { type: 'string' },
                        timePerf: { type: 'number' },
                        eighteenMonthPctToMid: { type: 'number' },
                        minervini: { type: 'boolean' },
                        msIndustryRank: { type: 'number' },
                        totalValue: { type: 'number'},
                    },
                },
            },
        };

        return dataSource;
    }

    protected getGridOptions(dataSource: any): kendo.ui.GridOptions {
        const options = {
            dataSource: dataSource,
            height: 890,
            groupable: false,
            sortable: {
                mode: "mixed",
                allowUnsort: true,
                showIndexes: true
            },
            filterable: true,
            pageable: false,
            detailInit: this.openDetail,
            dataBinding: () => {
                this.rowCounter = 0;
            },
            columns: [{
                title: `${MultiSourceAnalysis.GROUP_COMPANY}<br><input id='${MultiSourceAnalysis.COMPANY_SWITCH}' type='checkbox'> Switch`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        title: '#',
                        template: () => `${++this.rowCounter}`,
                        width: '3%',
                    },
                    {
                        field: this.fields['symbol'].getName(),
                        title: 'Symbol',
                        width: '4%',
                        filterable: false,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer',
                            'tooltip': this.fields['symbol'].getName()
                        },
                    }, {
                        field: this.fields['msCompanyName'].getName(),
                        title: 'MarketSurge<br>Company Name',
                        width: '12%',
                        hidden: true,
                        filterable: false,
                        myField: this.fields['msCompanyName'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12-left' },
                        attributes: { 'class': 'table-cell-tight', style: 'text-align: left' },
                    }, {
                        field: this.fields['vlCompanyName'].getName(),
                        title: 'ValueLine<br>Company Name',
                        width: '12%',
                        filterable: false,
                        myField: this.fields['vlCompanyName'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12-left' },
                        attributes: { 'class': 'table-cell-tight', style: 'text-align: left' },
                    }, {
                        field: this.fields['personalReportDate'].getName(),
                        title: 'Pers<br>Rpt',
                        width: '4%',
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer grid-column-group-separator',
                            'tooltip': this.fields['personalReportGrade'].getName()
                        },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['personalReportGrade'].getName(), null, 'O')),
                    },
                ],
            }, {
                title: `${MultiSourceAnalysis.GROUP_INDUSTRY}<br><input id='${MultiSourceAnalysis.INDUSTRY_SWITCH}' type='checkbox'> Switch`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['msIndustryName'].getName(),
                        title: 'MarketSurge<br>Name',
                        width: '12%',
                        hidden: true,
                        filterable: false,
                        myField: this.fields['msIndustryName'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12-left' },
                        attributes: { 'class': 'table-cell-tight', style: 'text-align: left' },
                    }, {
                        field: this.fields['msIndustryRank'].getName(),
                        title: 'Rank',
                        width: '4%',
                        hidden: true,
                        filterable: true,
                        myField: this.fields['msIndustryRank'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12-left table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                    }, {
                        field: this.fields['vlIndustryName'].getName(),
                        title: 'ValueLine<br>Name',
                        width: '12%',
                        filterable: false,
                        myField: this.fields['vlIndustryName'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12-left' },
                        attributes: { 'class': 'table-cell-tight', style: 'text-align: left' },
                    }, {
                        field: this.fields['vlIndustryRank'].getName(),
                        title: 'Rank',
                        width: '4%',
                        filterable: true,
                        myField: this.fields['vlIndustryRank'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12-left table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                    },
                ],
            }, {
                title: `${MultiSourceAnalysis.GROUP_PRICE}<br><input id='priceDetails' type='checkbox'> Details`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['currentPrice'].getName(),
                        title: 'Close',
                        width: '5%',
                        filterable: true,
                        myField: this.fields['currentPrice'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer',
                            'tooltip': this.fields['currentPrice'].getName()
                        },
                        format: '{0:c2}',
                    }, {
                        field: this.fields['dailyClosingRange'].getName(),
                        title: 'Daily<br>Range',
                        hidden: true,
                        width: '4%',
                        filterable: true,
                        myField: this.fields['dailyClosingRange'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['dailyClosingRange'].getName())),
                    }, {
                        field: this.fields['weeklyClosingRange'].getName(),
                        title: 'Weekly<br>Range',
                        hidden: true,
                        width: '4%',
                        filterable: true,
                        myField: this.fields['weeklyClosingRange'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['weeklyClosingRange'].getName())),
                    }, {
                        field: this.fields['priceVsTwentyOneDay'].getName(),
                        title: '% ><br>21D',
                        hidden: true,
                        width: '4%',
                        filterable: true,
                        myField: this.fields['priceVsTwentyOneDay'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getColorNumberTemplate(this.fields['priceVsTwentyOneDay'].getName(), 'n0')),
                    }, {
                        field: this.fields['priceVsFiftyDay'].getName(),
                        title: '% ><br>50D',
                        hidden: true,
                        width: '4%',
                        filterable: true,
                        myField: this.fields['priceVsFiftyDay'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getColorNumberTemplate(this.fields['priceVsFiftyDay'].getName(), 'n0')),
                    }, {
                        field: this.fields['priceVsTwoHundredDay'].getName(),
                        title: '% ><br>200D',
                        hidden: true,
                        width: '4%',
                        filterable: true,
                        myField: this.fields['priceVsTwoHundredDay'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                        template: kendo.template(TemplateHelpers.getColorNumberTemplate(this.fields['priceVsTwoHundredDay'].getName(), 'n0')),
                    }, {
                        field: this.fields['movingAveragesGrade'].getName(),
                        title: 'MA<br>Grd',
                        width: '4%',
                        filterable: true,
                        myField: this.fields['movingAveragesGrade'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer grid-column-group-separator',
                            'tooltip': this.fields['movingAveragesGrade'].getName()
                        },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['movingAveragesGrade'].getName())),
                    },
                ],
            }, {
                title: `${MultiSourceAnalysis.GROUP_TECH_RS}<br><input id='rsDetails' type='checkbox'> Details`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['relativeStrengthRating'].getName(),
                        title: '12M',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['relativeStrengthRating'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['relativeStrengthRating'].getName())),
                    }, {
                        field: this.fields['relativeStrengthSixMonthRating'].getName(),
                        title: '6M',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['relativeStrengthSixMonthRating'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['relativeStrengthSixMonthRating'].getName())),
                    }, {
                        field: this.fields['relativeStrengthThreeMonthRating'].getName(),
                        title: '3M',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['relativeStrengthThreeMonthRating'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['relativeStrengthThreeMonthRating'].getName())),
                    }, {
                        field: this.fields['accumDistRating'].getName(),
                        title: 'A/D',
                        width: '4%',
                        hidden: true,
                        myField: this.fields['accumDistRating'],
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['accumDistRatingGrade'].getName(),
                            this.fields['accumDistRating'].getName())),
                    }, {
                        field: this.fields['relativeStrengthStacked'].getName(),
                        title: 'Lines<br>Stack',
                        filterable: true,
                        width: '4%',
                        hidden: true,
                        myField: this.fields['relativeStrengthStacked'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight' },
                        template: kendo.template(TemplateHelpers.getGreenBoolTemplate(this.fields['relativeStrengthStacked'].getName(), 'Y')),
                    }, {
                        field: this.fields['relativeStrengthPositive'].getName(),
                        title: 'Lines<br>Pos',
                        filterable: true,
                        width: '4%',
                        hidden: true,
                        myField: this.fields['relativeStrengthPositive'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight' },
                        template: kendo.template(TemplateHelpers.getGreenBoolTemplate(this.fields['relativeStrengthPositive'].getName(), 'Y')),
                    }, {
                        field: this.fields['relativeStrengthLineNewHigh'].getName(),
                        title: 'New<br>High',
                        width: '4%',
                        hidden: true,
                        filterable: true,
                        myField: this.fields['relativeStrengthLineNewHigh'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getGreenBoolTemplate(this.fields['relativeStrengthLineNewHigh'].getName(), 'Y')),
                    }, {
                        field: this.fields['fullCurrentPctK'].getName(),
                        title: 'STO<br>%K',
                        width: '4%',
                        hidden: true,
                        filterable: true,
                        myField: this.fields['fullCurrentPctK'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['fullCurrentPctKGrade'].getName(),
                            this.fields['fullCurrentPctK'].getName())),
                    }, {
                        field: this.fields['relativeStrengthGrade'].getName(),
                        title: 'RS<br>Grd',
                        filterable: true,
                        width: '4%',
                        myField: this.fields['relativeStrengthGrade'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center table-cell-pointer', 'tooltip': this.fields['relativeStrengthGrade'].getName() },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['relativeStrengthGrade'].getName())),
                    }, {
                        field: this.fields['rs8WeekCrossoverGrade'].getName(),
                        title: '8-Wk<br>Cross',
                        filterable: true,
                        width: '4%',
                        myField: this.fields['rs8WeekCrossoverGrade'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center table-cell-pointer', 'tooltip': this.fields['rs8WeekCrossoverGrade'].getName() },
                        template: kendo.template(TemplateHelpers.getGreenGradeTemplate(
                            this.fields['rs8WeekCrossoverGrade'].getName(), this.fields['rs8WeekCrossoverWeeksAgo'].getName())),
                    }, {
                        field: this.fields['rs21WeekCrossoverGrade'].getName(),
                        title: '21-Wk<br>Cross',
                        filterable: true,
                        width: '4%',
                        myField: this.fields['rs21WeekCrossoverGrade'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer grid-column-group-separator',
                            'tooltip': this.fields['rs21WeekCrossoverGrade'].getName()
                        },
                        template: kendo.template(TemplateHelpers.getGreenGradeTemplate(
                            this.fields['rs21WeekCrossoverGrade'].getName(), this.fields['rs21WeekCrossoverWeeksAgo'].getName())),
                    },
                ],
            }, {
                title: `${MultiSourceAnalysis.GROUP_ATR}<br><input id='atrDetails' type='checkbox'> Details`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['twentyOneDayAtrPct'].getName(),
                        title: '21D',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['twentyOneDayAtrPct'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        format: '{0:n1}',
                    }, {
                        field: this.fields['thirtyDayAtrPct'].getName(),
                        title: '30D',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['thirtyDayAtrPct'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        format: '{0:n1}',
                    }, {
                        field: this.fields['fiftyDayAtrPct'].getName(),
                        title: '50D',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['fiftyDayAtrPct'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        format: '{0:n1}',
                    }, {
                        field: this.fields['earningsStability'].getName(),
                        title: 'Stabi<br>lity',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['earningsStability'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                    }, {
                        field: this.fields['atrGrade'].getName(),
                        title: 'Grd',
                        width: '4%',
                        filterable: true,
                        myField: this.fields['atrGrade'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer grid-column-group-separator',
                            'tooltip': this.fields['atrGrade'].getName()
                        },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['atrGrade'].getName())),
                    },
                ],
            }, {
                title: `${MultiSourceAnalysis.GROUP_EPS}<br><input id='epsDetails' type='checkbox'> Details`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['earningsPerShareRating'].getName(),
                        title: 'EPS<br>Rating',
                        filterable: true,
                        width: '4%',
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['earningsPerShareRating'].getName())),
                    }, {
                        field: this.fields['earningsPerShareGrade'].getName(),
                        title: 'EPS<br>Grd',
                        width: '4%',
                        filterable: true,
                        myField: this.fields['earningsPerShareGrade'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer grid-column-group-separator',
                            'tooltip': this.fields['earningsPerShareGrade'].getName()
                        },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['earningsPerShareGrade'].getName())),
                    }, {
                        field: this.fields['earningsPerSharePctGrowthThreeYr'].getName(),
                        title: '3 Yr<br>Grwth',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['earningsPerSharePctGrowthThreeYr'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                    }, {
                        field: this.fields['earningsPerShareEstCurYrPct'].getName(),
                        title: 'Curr<br>Year',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['earningsPerShareEstCurYrPct'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                    }, {
                        field: this.fields['earningsPerShareNextYrPct'].getName(),
                        title: 'Next<br>Year',
                        filterable: true,
                        hidden: true,
                        width: '4%',
                        myField: this.fields['earningsPerShareNextYrPct'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight  table-cell-center grid-column-group-separator' },
                    },
                ],
            }, {
                title: `${MultiSourceAnalysis.GROUP_MS_DATA}<br><input id='msDetails' type='checkbox'> Details`,
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['compositeRating'].getName(),
                        title: 'Comp<br>Rating',
                        width: '4%',
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['compositeRating'].getName())),
                    }, {
                        field: this.fields['smrRating'].getName(),
                        title: 'SMR<br>Rating',
                        width: '4%',
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['smrRating'].getName())),
                    }, {
                        field: this.fields['returnOnEquity'].getName(),
                        title: 'ROE',
                        width: '4%',
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(
                            this.fields['returnOnEquityGrade'].getName(), this.fields['returnOnEquity'].getName())),
                    }, {
                        field: this.fields['eventDate'].getName(),
                        title: 'Base<br>Form',
                        width: '4%',
                        filterable: true,
                        myField: this.fields['eventDate'],
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center table-cell-pointer', 'tooltip': this.fields['eventDate'].getName() },
                        template: kendo.template(TemplateHelpers.getGreenGradeTemplate(
                            this.fields['baseFormingGrade'].getName(), this.fields['baseFormingWeeksAgo'].getName())),
                    }, {
                        field: this.fields['numBlueDots'].getName(),
                        title: 'Blue<br>Dots',
                        width: '4%',
                        hidden: true,
                        myField: this.fields['numBlueDots'],
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                    }, {
                        field: this.fields['antsCount'].getName(),
                        title: 'Ants',
                        width: '4%',
                        hidden: true,
                        myField: this.fields['antsCount'],
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                    }, {
                        field: this.fields['yieldPct'].getName(),
                        title: 'Yield',
                        width: '4%',
                        hidden: true,
                        myField: this.fields['yieldPct'],
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        format: '{0:n0}',
                    }, {
                        field: this.fields['debtPct'].getName(),
                        title: 'Debt',
                        width: '4%',
                        hidden: true,
                        myField: this.fields['debtPct'],
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(
                            this.fields['debtPctGrade'].getName(), this.fields['debtPct'].getName())),
                    }, {
                        field: this.fields['incomeQualityGrade'].getName(),
                        title: 'Grd',
                        width: '4%',
                        myField: this.fields['incomeQualityGrade'],
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: {
                            'class': 'table-cell-tight table-cell-center table-cell-pointer grid-column-group-separator',
                            'tooltip': this.fields['incomeQualityGrade'].getName()
                        },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(this.fields['incomeQualityGrade'].getName(),
                            this.fields['incomeQualityGrade'].getName(), null, this.fields['numBlueDots'].getName(),
                            this.fields['antsCount'].getName(), this.fields['yieldPct'].getName())),
                    },
                ],
            }, {
                title: 'VL Data',
                headerAttributes: { class: 'table-header-cell grid-header-14 grid-column-group-separator' },
                columns: [
                    {
                        field: this.fields['timePerf'].getName(),
                        title: 'Time',
                        width: '4%',
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                        attributes: { 'class': 'table-cell-tight table-cell-center' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(
                            this.fields['timePerfGrade'].getName(), this.fields['timePerf'].getName())),
                    }, {
                        field: this.fields['eighteenMonthPctToMid'].getName(),
                        title: '18 Mo<br>To Mid',
                        width: '4%',
                        filterable: true,
                        headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter grid-column-group-separator' },
                        attributes: { 'class': 'table-cell-tight table-cell-center grid-column-group-separator' },
                        template: kendo.template(TemplateHelpers.getGradeTemplate(
                            this.fields['eighteenMonthPctToMidGrade'].getName(), this.fields['eighteenMonthPctToMid'].getName())),
                    },
                ],
            }, {
                field: this.fields['minervini'].getName(),
                title: 'Miner<br>vini',
                width: '4%',
                filterable: true,
                headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getGreenBoolTemplate(this.fields['minervini'].getName(), 'Y')),
            }, {
                field: this.fields['totalValue'].getName(),
                title: 'Rank',
                width: '4%',
                filterable: true,
                headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getNormalizedTemplate(this.fields['totalValue'].getName())),
            }],
        }

        return options;
    }

    protected gridDataBound() {
        this.initTooltipData();
        this.defineEvents();
    }

    protected initTooltipData() {
        const firstDataRow = 2;
        const symbolColumn = 2;

        const data = this.grid.dataSource.data();
        if (!data || data.length == 0) {
            console.error('initTooltipData(): no data');
            return;
        }

        const rows = $(this.gridId).find('tr');
        if (rows?.length <= firstDataRow) {
            return;
        }

        let ttCellIndices = TooltipHelper.getCellIndices(rows, firstDataRow);
        if ($.isEmptyObject(ttCellIndices)) {
            return;
        }

        rows.each((rowIndex: number, rowElement: HTMLTableRowElement) => {
            const cellElements = rowElement.cells;

            // Skip over the group header rows.
            if (rowIndex < firstDataRow) {
                return;
            }

            const rowData = TooltipHelper.getRowData(
                cellElements[symbolColumn].innerHTML, data);
            if (!rowData) {
                return;
            }

            // Get the data to be displayed in this row's tooltips and pass it into
            // TooltipHelper so it can format it and set up the Kendo Tooltips.
            const ttContent = new Array<TooltipColumnContent>();
            let ttRowContent: TooltipColumnContent;

            if (rowData.msCompanyDescription?.length) {
                const cell = cellElements[ttCellIndices['symbol']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement(
                    'Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement(
                    'Company Name', rowData.msCompanyName));
                ttRowContent.ttElement.push(new TooltipElement(
                    'Description', rowData.msCompanyDescription));
                ttContent.push(ttRowContent);
            }

            const perCell = cellElements[ttCellIndices['personalReportGrade']];
            ttRowContent = new TooltipColumnContent();
            ttRowContent.cellElement = perCell;
            ttRowContent.ttElement = [];
            ttRowContent.ttElement.push(new TooltipElement(
                'Symbol', rowData.symbol));
            if (rowData.personalReportGrade) {
                ttRowContent.ttElement.push(new TooltipElement(
                    'Personal Report', 'pdf', null, rowData.personalUrl));
                ttRowContent.ttElement.push(new TooltipElement(
                    'Personal Report Date', rowData.personalReportDate, 'date'));
                ttContent.push(ttRowContent);
            }
            ttRowContent.ttElement.push(new TooltipElement(
                'Original Report', 'pdf', null, rowData.originalUrl));
            ttRowContent.ttElement.push(new TooltipElement(
                'Original Report Date', rowData.originalReportDate, 'date'));
            ttContent.push(ttRowContent);

            if (rowData.currentPrice) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['currentPrice']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('Daily Closing Range',
                    kendo.toString(rowData.dailyClosingRange / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('Weekly Closing Range',
                    kendo.toString(rowData.weeklyClosingRange / 100, 'p0')));
                ttContent.push(ttRowContent);
            }

            if (rowData.movingAveragesGrade) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['movingAveragesGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('Price vs. 21-Day MA',
                    kendo.toString(rowData.priceVsTwentyOneDay / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('Price vs. 50-Day MA',
                    kendo.toString(rowData.priceVsFiftyDay / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('Price vs. 200-Day MA',
                    kendo.toString(rowData.priceVsTwoHundredDay / 100, 'p0')));
                ttContent.push(ttRowContent);
            }

            if (rowData.relativeStrengthGrade) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['relativeStrengthGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('Relative Strength 12 Month',
                    kendo.toString(rowData.relativeStrengthRating, 'n0')));
                ttRowContent.ttElement.push(new TooltipElement('Relative Strength 6 Month',
                    kendo.toString(rowData.relativeStrengthSixMonthRating, 'n0')));
                ttRowContent.ttElement.push(new TooltipElement('Relative Strength 3 Month',
                    kendo.toString(rowData.relativeStrengthThreeMonthRating, 'n0')));
                ttRowContent.ttElement.push(new TooltipElement('Accumulation/Distribution',
                    rowData.accumDistRating));
                ttRowContent.ttElement.push(new TooltipElement('Stochastics Full Current %K',
                    rowData.fullCurrentPctK));
                ttContent.push(ttRowContent);
            }

            if (rowData.rs8WeekCrossoverGrade) {
                ttRowContent = new TooltipColumnContent();
                let cell = cellElements[ttCellIndices['rs8WeekCrossoverGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('RS 8 Week Crossover Date',
                    rowData.rs8WeekCrossoverDate, 'date'));
                ttRowContent.ttElement.push(new TooltipElement('RS 21 Week Crossover Date',
                    rowData.rs21WeekCrossoverDate, 'date'));
                ttContent.push(ttRowContent);
            }

            if (rowData.rs21WeekCrossoverGrade) {
                ttRowContent = new TooltipColumnContent();
                let cell = cellElements[ttCellIndices['rs21WeekCrossoverGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('RS 8 Week Crossover Date',
                    rowData.rs8WeekCrossoverDate, 'date'));
                ttRowContent.ttElement.push(new TooltipElement('RS 21 Week Crossover Date',
                    rowData.rs21WeekCrossoverDate, 'date'));
                ttContent.push(ttRowContent);
            }

            if (rowData.atrGrade) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['atrGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('21-Day ATR',
                    kendo.toString(rowData.twentyOneDayAtrPct / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('30-Day ATR',
                    kendo.toString(rowData.thirtyDayAtrPct / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('50-Day ATR',
                    kendo.toString(rowData.fiftyDayAtrPct / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('Earnings Stability',
                    kendo.toString(rowData.earningsStability, 'n0')));
                ttContent.push(ttRowContent);
            }

            if (rowData.earningsPerShareGrade) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['earningsPerShareGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('Three Year Growth',
                    kendo.toString(rowData.earningsPerSharePctGrowthThreeYr / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('Current Year Growth',
                    kendo.toString(rowData.earningsPerShareEstCurYrPct / 100, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement('Next Year Growth',
                    kendo.toString(rowData.earningsPerShareNextYrPct / 100, 'p0')));
                ttContent.push(ttRowContent);
            }

            if (rowData.eventDate) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['eventDate']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Symbol', rowData.symbol));
                ttRowContent.ttElement.push(new TooltipElement('Base Type and Stage',
                    rowData.baseTypeStage));
                ttRowContent.ttElement.push(new TooltipElement('Event Date',
                    rowData.eventDate, 'date'));
                ttRowContent.ttElement.push(new TooltipElement('Weekly % Change vs. Pivot',
                    kendo.toString(rowData.weeklyPctChgVsPivot, 'p0')));
                ttContent.push(ttRowContent);
            }

            if (rowData.incomeQualityGrade) {
                ttRowContent = new TooltipColumnContent();
                const cell = cellElements[ttCellIndices['incomeQualityGrade']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement('Number of Blue Dots',
                    rowData.numBlueDots));
                ttRowContent.ttElement.push(new TooltipElement('Number of Ants',
                    rowData.antsCount));
                ttRowContent.ttElement.push(new TooltipElement('Yield Percent',
                    rowData.yieldPct, 'p0'));
                ttRowContent.ttElement.push(new TooltipElement('Debt Percent',
                    rowData.debtPct, 'p0'));
                ttContent.push(ttRowContent);
            }

            TooltipHelper.createRowOfTooltips(ttContent);
        });
    }

    protected defineEvents() {
        $('#filters').off('change').on('change', (e: any) => {
            this.updateFilter(e.currentTarget.value);
        });

        $(`#${MultiSourceAnalysis.COMPANY_SWITCH}`).off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_COMPANY, state);
        });

        $(`#${MultiSourceAnalysis.INDUSTRY_SWITCH}`).off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_INDUSTRY, state);
        });

        $('#priceDetails').off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_PRICE, state);
        });

        $('#rsDetails').off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_TECH_RS, state);
        });

        $('#atrDetails').off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_ATR, state);
        });

        $('#epsDetails').off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_EPS, state);
        });

        $('#msDetails').off('change').on('change', (e: any) => {
            const state = e.currentTarget.checked;
            this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_MS_DATA, state);
        });
    }

    // Each .cshtml page that uses this class should define these filter sets in a dropdown.
    protected updateFilter(value: string): boolean {
        switch (value) {
            case 'none':
                this.filterSet.clear();
                return true;
            case 'monthly':
                this.filterSet.set(MultiSourceAnalysis.FILTER_MONTHLY);
                return true;
            case 'weekly':
                this.filterSet.set(MultiSourceAnalysis.FILTER_WEEKLY);
                return true;
            case 'webby':
                this.filterSet.set(MultiSourceAnalysis.FILTER_WEBBY);
                return true;
        }

        return false;
    }

    public getGridId() {
        return this.gridId;
    }

    private openDetail(e: any) {
        let urlSuffix = GlobalData.URL_SUFFIX_MULTI_SOURCE_MULTI_SOURCE_ANALYSIS +
            '?symbol=' + e.data.symbol;

        let success = (data: any) => {
            if (e.detailCell != null) {
                e.detailCell.html(data);
            }
        };

        let error = () => {
            e.detailCell = null;
        }

        Ajax.invoke(urlSuffix, 'GET', null, success, error);
    }

    protected switchCompanyAndIndustry(checked: boolean) {
        $(`#${MultiSourceAnalysis.COMPANY_SWITCH}`).prop('checked', checked);
        this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_COMPANY, checked);
        $(`#${MultiSourceAnalysis.INDUSTRY_SWITCH}`).prop('checked', checked);
        this.filterSet.updateGroupState(MultiSourceAnalysis.GROUP_INDUSTRY, checked);
    }

    // TODO: see if we can delete this
    protected finishInit() {
        const data = this.grid.dataSource.data();
        if (data?.length > 0) {
            ReportDateHelper.showReportDate(data[0].closingDate);

            for (let i = 0; i < data.length; ++i) {
                this.setCurrPosition(data[i]);
            }
            this.grid.refresh();
        }
    }

    // TODO: see if we can delete this
    private setCurrPosition(row: any) {
        const maxInvestmentPerStock = row.maxInvestment / row.maxStocks;

        row.calcCurrPosition = row.sumQtyOwn ?
            row.sumQtyOwn * row.close / maxInvestmentPerStock : '';
    }

    public destroy() {
        this.grid.destroy();
        $(this.gridId).empty();
    }
}
